import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface HeroImageSettings {
  image_file: string;
  mobile_height: string;
  desktop_height: string;
  mobile_position_x: string;
  mobile_position_y: string;
  desktop_position_x: string;
  desktop_position_y: string;
  object_fit: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
}

const getDefaultSettings = (_pageKey: string): HeroImageSettings => {
  return {
    image_file: '',
    mobile_height: '50vh',
    desktop_height: '60vh',
    mobile_position_x: '50',
    mobile_position_y: '50',
    desktop_position_x: '50',
    desktop_position_y: '50',
    object_fit: 'cover'
  };
};

export const useHeroImageSettings = (pageKey: string) => {
  const queryClient = useQueryClient();
  const defaultSettings = getDefaultSettings(pageKey);

  const { data: settings, isLoading, error } = useQuery({
    queryKey: ['hero-settings', pageKey],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('settings')
        .select('value')
        .eq('key', `hero_image_${pageKey}`)
        .single();

      if (error) {
        console.warn(`Could not fetch hero settings for ${pageKey}:`, error);
        return defaultSettings;
      }

      try {
        return JSON.parse(data.value) as HeroImageSettings;
      } catch (parseError) {
        console.warn(`Could not parse hero settings for ${pageKey}:`, parseError);
        return defaultSettings;
      }
    },
    staleTime: 1 * 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
  });

  const updateSettings = async (newSettings: Partial<HeroImageSettings>) => {
    const currentSettings = settings || defaultSettings;
    const updatedSettings = { ...currentSettings, ...newSettings };

    const { error } = await supabase
      .from('settings')
      .upsert({
        key: `hero_image_${pageKey}`,
        value: JSON.stringify(updatedSettings),
        description: `Hero image settings for ${pageKey} page`
      }, {
        onConflict: 'key'
      });

    if (error) {
      console.error('Error updating hero settings:', error);
      throw new Error(`Kunde inte spara inställningar: ${error.message}`);
    }

    queryClient.invalidateQueries({ queryKey: ['hero-settings', pageKey] });
    return updatedSettings;
  };

  const getImageUrl = (imageFile?: string) => {
    // Return null while loading settings to prevent flash of default image
    if (isLoading && !settings) {
      return null;
    }
    
    const file = imageFile || settings?.image_file || '';

    // No image set — render placeholder instead of falling back
    if (!file) return null;

    // If a full URL is provided, return as is
    if (/^https?:\/\//i.test(file)) {
      return file;
    }

    // If it's a local path (starts with /), return as is
    if (file.startsWith('/')) {
      return file;
    }

    // Supabase storage file
    const { data } = supabase.storage.from('images').getPublicUrl(file);
    return data?.publicUrl || null;
  };

  return {
    settings: settings || defaultSettings,
    isLoading,
    error,
    updateSettings,
    getImageUrl
  };
};